/**
 * Input destinatari con autocompletamento dalla Rubrica.
 *
 * Mantiene il modello del Composer (stringa separata da virgole): suggerisce
 * sul token corrente (testo dopo l'ultima virgola) e, alla selezione, lo
 * sostituisce con l'email del contatto.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { contactsApi } from '@/lib/api/endpoints';
import { cn } from '@/lib/utils';

export interface RecipientInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function RecipientInput({ value, onChange, placeholder, className }: RecipientInputProps) {
  const [focused, setFocused] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [debouncedToken, setDebouncedToken] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Token corrente = testo dopo l'ultima virgola/punto e virgola
  const token = useMemo(() => {
    const parts = value.split(/[,;]/);
    return (parts[parts.length - 1] ?? '').trim();
  }, [value]);

  // Indirizzi già inseriti (per non risuggerirli)
  const already = useMemo(
    () =>
      new Set(
        value
          .split(/[,;]/)
          .slice(0, -1)
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean)
      ),
    [value]
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedToken(token), 200);
    return () => clearTimeout(t);
  }, [token]);

  const suggestQuery = useQuery({
    queryKey: ['contact-suggest', debouncedToken],
    queryFn: () => contactsApi.suggest(debouncedToken),
    enabled: focused && debouncedToken.length >= 2,
    staleTime: 30_000,
  });

  const suggestions = useMemo(
    () =>
      (suggestQuery.data?.contacts ?? []).filter(
        (c) => !already.has(c.email.toLowerCase())
      ),
    [suggestQuery.data, already]
  );

  const showDropdown = focused && debouncedToken.length >= 2 && suggestions.length > 0;

  useEffect(() => {
    setHighlighted(0);
  }, [debouncedToken, suggestions.length]);

  function pick(email: string) {
    const parts = value.split(/([,;])/); // mantiene i separatori
    // Sostituisce l'ultimo segmento non-separatore col contatto scelto
    for (let i = parts.length - 1; i >= 0; i--) {
      if (parts[i] !== ',' && parts[i] !== ';') {
        parts[i] = ` ${email}`;
        break;
      }
    }
    const next = parts.join('').replace(/^\s+/, '');
    onChange(`${next}, `);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      const sel = suggestions[highlighted];
      if (sel) {
        e.preventDefault();
        pick(sel.email);
      }
    } else if (e.key === 'Escape') {
      setFocused(false);
    }
  }

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)} // lascia il tempo al click sul suggerimento
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
      />
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-popover text-popover-foreground border rounded-md shadow-md max-h-56 overflow-y-auto py-1">
          {suggestions.map((c, i) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault(); // evita il blur prima del click
                pick(c.email);
              }}
              onMouseEnter={() => setHighlighted(i)}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm',
                i === highlighted ? 'bg-accent' : ''
              )}
            >
              <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-primary text-[11px] font-bold flex-shrink-0">
                {(c.name || c.email).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate">
                  {c.name || c.email}
                  {c.is_favorite && <Star className="inline h-3 w-3 ml-1 text-amber-500 fill-amber-500" />}
                </div>
                {c.name && (
                  <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
